// Google Drive screen pieces. Words: SITE_FOLDERS_WORDS.md rev 3.1. Mockup: MOCKUP_DRIVE_FOLDERS.html rev 2 (approved 2026-10-22).
// Pure: strings in, HTML out. Unit tested: tests/unit/drive-screens.test.ts.
// Colors follow the survey: amber = do this, blue = go there, green = done, grey = not started.

import type { DriveSettings, SiteDriveFolder } from "./drive-folders";
import { esc, ident } from "./training-screens";

/** What the page knows about Drive. Null settings: never connected, or not read yet. */
export interface DriveView {
  settings: Pick<DriveSettings, "connected" | "rootFolderId"> | null;
}

/** A site's folder counts only when it was built under the top folder in use now (Change moves the work). */
export function siteHasFolder(view: DriveView, folder: Pick<SiteDriveFolder, "id"> & { rootFolderId?: string } | null | undefined): boolean {
  return !!folder?.id && !!view.settings?.rootFolderId && folder.rootFolderId === view.settings.rootFolderId;
}

// ---- Panel A: Pick a site ----

/** One amber line, only until Drive is connected, only for an admin covering all sites. */
export function driveNotConnectedLine(view: DriveView, coversAll: boolean): string {
  if (!coversAll || view.settings?.connected) return "";
  return `<div class="dv-line">Google Drive is not connected. <a href="#/settings/drive">Connect it in Program Settings</a></div>`;
}

/** Status only: green Drive folder, grey No folder. Nothing at all while Drive was never connected. */
export function drivePill(view: DriveView, hasFolder: boolean): string {
  if (!view.settings) return "";
  return hasFolder ? '<span class="pill green">Drive folder</span>' : '<span class="pill grey">No folder</span>';
}

// ---- Panel B: the chip next to the site name ----

/**
 * Amber Build Folder when the site has none, blue Drive Folder once built. Same place, so the bar never shifts.
 * Waiting: the ask is on the iPad, offline, or the server is still building.
 */
export function driveChip(v: { view: DriveView; folderUrl: string | null; waiting: boolean; reason: string }): string {
  if (!v.view.settings?.connected) return "";
  if (v.folderUrl) return `<a class="dv-chip go" href="${esc(v.folderUrl)}" target="_blank" rel="noopener">Drive Folder</a>`;
  if (v.waiting) return '<span class="dv-chip wait">Building Folder…</span>';
  return `<button class="dv-chip do" type="button" data-dv="build"${v.reason ? ` title="${esc(v.reason)}"` : ""}>Build Folder</button>`;
}

// ---- Panel C: the line under Download ----

export type SaveState = "off" | "waiting" | "saved" | "failed";

/** The Saved to Drive line. The saved path is the link: no second Open Folder button. */
export function driveSavedLine(v: { state: SaveState; folderPath: string; folderUrl: string; reason: string; fileName: string }): string {
  if (v.state === "off") return "";
  if (v.state === "saved") {
    return `<div class="dv-saved"><span class="pill green">Saved to Drive</span><a href="${esc(v.folderUrl)}" target="_blank" rel="noopener">${esc(v.folderPath)}</a></div>`;
  }
  if (v.state === "failed") {
    return `<div class="dv-saved"><span class="pill red">Not saved to Drive</span><small>${esc(v.reason)}</small><button class="dv-tbtn" type="button" data-dv="retry" data-file="${esc(v.fileName)}">Try Again</button></div>`;
  }
  return '<div class="dv-saved"><span class="pill amber">Waiting for Drive</span><small>Saves on signal. Builds the folder first if it has none.</small></div>';
}

// ---- Panel D: Program Settings › Google Drive ----

export function driveMenuLine(s: Partial<DriveSettings> | null): string {
  if (!s?.connected) return "Not connected";
  return `${s.account || "Connected"} · ${s.rootFolderName || "no top folder picked"}`;
}

export interface DriveSettingsView {
  settings: Partial<DriveSettings> | null;
  busy: "" | "connect" | "change" | "disconnect";
  error: string;
  /** The Change box is open. */
  changing: boolean;
  /** Disconnect asks once. */
  confirming: boolean;
  /** From the sign-in coming back: "connected" or "failed" with the words why. */
  back: { result: string; why: string } | null;
  /** The settings read failed: show Try Again, not the Connect card. */
  retry?: boolean;
}

export function driveSettingsPage(v: DriveSettingsView): string {
  const s = v.settings;
  const head = `<a class="back" href="#/settings">Program Settings</a>${ident("GD", "Google Drive", "Site folders and label PDFs save here")}`;
  const backNote = v.back
    ? v.back.result === "connected"
      ? '<div class="note green">Google Drive is connected.</div>'
      : `<div class="note red">Drive did not connect. ${esc(v.back.why || "Try Connect Drive again.")}</div>`
    : "";
  const err = v.error ? `<div class="note red">${esc(v.error)}</div>` : "";
  if (v.retry) {
    return `${head}${err}<button class="prim" type="button" data-dv="reload">Try Again</button>`;
  }
  if (!s?.connected) {
    return `${head}${backNote}${err}
      <div class="box"><div class="dv-row"><span>Not connected<small>Label PDFs and site folders save to Drive once connected.</small></span>
        <button class="prim" type="button" data-dv="connect"${v.busy ? " disabled" : ""}>${v.busy === "connect" ? "Opening Google…" : "Connect Drive"}</button></div></div>
      <div class="note">Sign in with the Google account that owns Active Solutions Master. The app finds that folder on its own.</div>
      `;
  }
  const folder = s.rootFolderId
    ? `<a href="https://drive.google.com/drive/folders/${esc(s.rootFolderId)}" target="_blank" rel="noopener">${esc(s.rootFolderName || "Open folder")}</a>`
    : '<span class="dv-need">No top folder picked</span>';
  const change = v.changing
    ? `<div class="dv-row col"><label for="dv-folder">Paste the top folder's Drive link</label>
        <input id="dv-folder" class="dv-input" autocomplete="off" autocapitalize="off" spellcheck="false" placeholder="https://drive.google.com/drive/folders/…">
        <div class="dv-btns"><button class="dv-tbtn" type="button" data-dv="change-cancel">Cancel</button><button class="prim" type="button" data-dv="change-save"${v.busy ? " disabled" : ""}>${v.busy === "change" ? "Checking…" : "Use This Folder"}</button></div>
        <small>Sites find or make their folder there on the next Build Folder. Nothing moves in Drive.</small></div>`
    : "";
  const disconnect = v.confirming
    ? `<div class="dv-row col"><b>Disconnect Google Drive?</b><small>Saving to Drive stops. Every file already in Drive stays.</small>
        <div class="dv-btns"><button class="dv-tbtn" type="button" data-dv="disconnect-cancel">Cancel</button><button class="dv-tbtn red" type="button" data-dv="disconnect-yes"${v.busy ? " disabled" : ""}>${v.busy === "disconnect" ? "Disconnecting…" : "Disconnect"}</button></div></div>`
    : `<div class="dv-row"><span>Disconnect<small>Stops saving to Drive. Files already in Drive stay.</small></span><button class="dv-tbtn red" type="button" data-dv="disconnect">Disconnect</button></div>`;
  return `${head}${backNote}${err}
    <div class="box">
      <div class="dv-row"><span>Account<small>${esc(s.account || "")}</small></span><span class="pill green">Connected</span></div>
      <div class="dv-row"><span>Folder<small>${folder}</small></span>${v.changing ? "" : '<button class="dv-tbtn" type="button" data-dv="change">Change</button>'}</div>
      ${change}
      ${disconnect}
    </div>
    `;
}
